import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowDown } from 'lucide-react';
import { useCursorContext } from '../hooks/useCursorContext';
import { nikoInfo } from '../data/portfolioData';
import { TextScramble } from './Preloader/TextScramble';
import { BackgroundRipple } from './BackgroundRipple';

gsap.registerPlugin(ScrollTrigger);

interface HeroProps {
  isLoaded: boolean;
}

export function Hero({ isLoaded }: HeroProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const scrollIndicatorRef = useRef<HTMLDivElement>(null);
  const parallaxBgRef = useRef<HTMLDivElement>(null);
  const greenOverlayRef = useRef<HTMLDivElement>(null);
  const impactDotRef = useRef<HTMLDivElement>(null);
  const impactRingRef = useRef<HTMLDivElement>(null);
  const impactRing2Ref = useRef<HTMLDivElement>(null);
  const impactGlowRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const nikoRef = useRef<HTMLSpanElement>(null);
  const knezRef = useRef<HTMLSpanElement>(null);
  const { setCursorState } = useCursorContext();
  const [triggerScramble, setTriggerScramble] = useState(false);
  const [triggerRipple, setTriggerRipple] = useState(false);

  // Track if this is a navigation back (isLoaded was already true on mount)
  // vs fresh load after preloader (isLoaded transitions false → true)
  const isNavigationBack = useRef(isLoaded);

  // Random tech/design words for placeholders (4 letters each to match NIKO/KNEZ)
  // Selected once on mount - no cycling, just random per page load
  const [placeholderWords] = useState(() => {
    const techWords = ['CODE', 'TECH', 'NEXT', 'FLOW', 'GRID', 'TYPE', 'FONT', 'DARK', 'VOID', 'FAST', 'FLEX', 'SCSS', 'HTML', 'JSON', 'HTTP', 'REST', 'APIS'];
    const designWords = ['LOVE', 'IDEA', 'FORM', 'BOLD', 'PURE', 'EDGE', 'SOFT', 'GLOW', 'NOIR', 'WAVE', 'MIND', 'ICON', 'LOOK', 'VIBE', 'COOL', 'FIRE', 'GOLD'];
    return {
      word1: techWords[Math.floor(Math.random() * techWords.length)],
      word2: designWords[Math.floor(Math.random() * designWords.length)],
    };
  });

  useEffect(() => {
    if (!isLoaded) return;

    const ctx = gsap.context(() => {
      // Check if this is a navigation back to homepage (not fresh load after preloader)
      // If so, skip the greenOverlay → dot animation and show final state immediately
      if (isNavigationBack.current) {
        // Hide greenOverlay immediately
        gsap.set(greenOverlayRef.current, { opacity: 0 });
        // Show elements in final state
        gsap.set([nikoRef.current, knezRef.current], { opacity: 1 });
        gsap.set(gridRef.current, { opacity: 1 });
        gsap.set('.corner-bracket', { opacity: 1, scale: 1 });
        gsap.set(impactDotRef.current, { scale: 1, opacity: 1 });
        gsap.set(subtitleRef.current, { y: 0, opacity: 1 });
        gsap.set(scrollIndicatorRef.current, { opacity: 1, y: 0 });
        // Trigger scramble immediately to show final text
        setTriggerScramble(true);

        // Only keep the scroll indicator bounce animation
        gsap.to(scrollIndicatorRef.current, {
          y: 10,
          duration: 1.5,
          repeat: -1,
          yoyo: true,
          ease: 'power1.inOut',
        });

        // Parallax background on scroll
        gsap.to(parallaxBgRef.current, {
          y: 200,
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 1,
          },
        });

        return;
      }

      // Full animation for fresh load after preloader
      const tl = gsap.timeline();

      // Letters are already visible with placeholder text (cycling)
      // Show them immediately when green starts contracting
      tl.set([nikoRef.current, knezRef.current], { opacity: 1 }, 0);

      // Grid reveal: fades in as green contracts
      tl.fromTo(
        gridRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.6, ease: 'power2.out' },
        0.3
      );

      // Corner brackets animate in from outside
      tl.fromTo(
        '.corner-bracket',
        { opacity: 0, scale: 1.5 },
        { opacity: 1, scale: 1, duration: 0.4, stagger: 0.05, ease: 'back.out(2)' },
        0.6
      );

      // Calculate where the dot element is positioned (target for the overlay)
      const dotRect = impactDotRef.current?.getBoundingClientRect();
      const targetX = dotRect ? dotRect.left + dotRect.width / 2 : window.innerWidth / 2;
      const targetY = dotRect ? dotRect.top + dotRect.height / 2 : window.innerHeight / 2;

      // Phase 1: greenOverlay shrinks AND moves from screen center to dot position
      // This creates the illusion that the full screen green "becomes" the dot
      tl.to(
        greenOverlayRef.current,
        {
          scale: 0.005, // Very small
          left: targetX,
          top: targetY,
          duration: 0.9,
          ease: 'power3.inOut',
          onComplete: () => {
            // Hide the overlay, it has arrived at the dot position
            gsap.set(greenOverlayRef.current, { opacity: 0 });
          },
        },
        0
      );

      // Phase 2: The actual dot appears as greenOverlay arrives
      tl.fromTo(
        impactDotRef.current,
        { scale: 0, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 0.3,
          ease: 'back.out(2)',
        },
        0.7 // Starts when greenOverlay is almost at the dot position
      );

      // Phase 3: Dot pulse up (signal it arrived)
      tl.to(
        impactDotRef.current,
        {
          scale: 1.5,
          duration: 0.2,
          ease: 'power2.out',
        },
        1.0 // Right after dot appears (0.7 + 0.3)
      );

      // Phase 4: Dot settles back to normal - EVENT DRIVEN from here
      tl.to(impactDotRef.current, {
        scale: 1,
        duration: 0.25,
        ease: 'back.out(2)',
        onComplete: () => {
          // Dot is settled - wait 0.5s then trigger shockwave
          gsap.delayedCall(0.5, () => {
            // Create shockwave timeline
            const waveTL = gsap.timeline();

            // Shockwave ring 1 - starts tiny at dot, expands outward
            waveTL.fromTo(
              impactRingRef.current,
              { scale: 1, opacity: 1, x: '-50%', y: '-50%' },
              {
                scale: 180,
                opacity: 0,
                x: '-50%',
                y: '-50%',
                duration: 0.8,
                ease: 'power2.out',
              },
              0
            );

            // Shockwave ring 2 - second ripple (slightly delayed)
            waveTL.fromTo(
              impactRing2Ref.current,
              { scale: 1, opacity: 0.8, x: '-50%', y: '-50%' },
              {
                scale: 200,
                opacity: 0,
                x: '-50%',
                y: '-50%',
                duration: 0.7,
                ease: 'power2.out',
              },
              0.08
            );

            // Glow burst - starts at dot
            waveTL.fromTo(
              impactGlowRef.current,
              { scale: 1, opacity: 1, x: '-50%', y: '-50%' },
              {
                scale: 80,
                opacity: 0,
                x: '-50%',
                y: '-50%',
                duration: 0.5,
                ease: 'power2.out',
              },
              0
            );

            // When wave hits letters (scale ~4-5, about 150ms into animation)
            // Trigger text scramble AND background ripple
            waveTL.call(
              () => {
                setTriggerScramble(true);
                setTriggerRipple(true);
              },
              [],
              0.15 // Wave reaches letters at ~15% of animation
            );
          });
        },
      });

      // Subtitle fade in (will be adjusted by wave timing)
      gsap.fromTo(
        subtitleRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, delay: 2.5, ease: 'power2.out' }
      );

      // Scroll indicator fade in
      gsap.fromTo(
        scrollIndicatorRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.5, delay: 3, ease: 'power2.out' }
      );

      // Scroll indicator bounce animation (after fade in)
      gsap.to(scrollIndicatorRef.current, {
        y: 10,
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
        delay: 3.5,
      });

      // Parallax background on scroll
      gsap.to(parallaxBgRef.current, {
        y: 200,
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
      });
    }, containerRef);

    return () => ctx.revert();
  }, [isLoaded]);

  return (
    <section
      ref={containerRef}
      className="relative h-screen flex items-center justify-center overflow-hidden"
    >
      {/* GREEN OVERLAY - contracts from full screen to dot */}
      {/* Must be a perfect circle (square with border-radius) large enough to cover viewport */}
      <div
        ref={greenOverlayRef}
        className="fixed z-[100] bg-emerald-500 pointer-events-none rounded-full"
        style={{
          width: '150vmax',
          height: '150vmax',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) scale(1)',
        }}
      />

      {/* Parallax background elements */}
      <div ref={parallaxBgRef} className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Aceternity-style Background Gradient Animation - subtle and dark */}
        <div className="absolute inset-0" style={{ filter: 'blur(100px)' }}>
          {/* Gradient blob 1 - emerald primary */}
          <div
            className="absolute w-[80vmax] h-[80vmax] rounded-full"
            style={{
              background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.15) 0%, transparent 50%)',
              top: '-20%',
              left: '-20%',
              animation: 'moveInCircle 20s reverse infinite',
              mixBlendMode: 'screen',
            }}
          />
          {/* Gradient blob 2 - emerald light */}
          <div
            className="absolute w-[70vmax] h-[70vmax] rounded-full"
            style={{
              background: 'radial-gradient(circle at center, rgba(52, 211, 153, 0.12) 0%, transparent 50%)',
              top: '0%',
              right: '-20%',
              animation: 'moveVertical 30s ease infinite',
              mixBlendMode: 'screen',
            }}
          />
          {/* Gradient blob 3 - teal accent */}
          <div
            className="absolute w-[60vmax] h-[60vmax] rounded-full"
            style={{
              background: 'radial-gradient(circle at center, rgba(20, 184, 166, 0.1) 0%, transparent 50%)',
              bottom: '-10%',
              left: '10%',
              animation: 'moveInCircle 40s linear infinite',
              mixBlendMode: 'screen',
            }}
          />
          {/* Gradient blob 4 - dark emerald */}
          <div
            className="absolute w-[90vmax] h-[90vmax] rounded-full"
            style={{
              background: 'radial-gradient(circle at center, rgba(6, 95, 70, 0.18) 0%, transparent 50%)',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              animation: 'moveHorizontal 40s ease infinite',
              mixBlendMode: 'screen',
            }}
          />
          {/* Gradient blob 5 - bright accent */}
          <div
            className="absolute w-[50vmax] h-[50vmax] rounded-full"
            style={{
              background: 'radial-gradient(circle at center, rgba(110, 231, 183, 0.08) 0%, transparent 50%)',
              top: '20%',
              left: '30%',
              animation: 'moveInCircle 25s reverse infinite',
              mixBlendMode: 'screen',
            }}
          />
        </div>

        {/* Keyframe styles for gradient animations */}
        <style>{`
          @keyframes moveInCircle {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes moveVertical {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-50vh); }
          }
          @keyframes moveHorizontal {
            0%, 100% { transform: translate(-50%, -50%) translateX(0); }
            50% { transform: translate(-50%, -50%) translateX(50vw); }
          }
        `}</style>

        {/* Grid Design System - Hero only */}
        <div ref={gridRef} className="absolute inset-0 opacity-0" aria-hidden="true">
          {/* Animated vertical grid lines */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(
                90deg,
                transparent 0px,
                transparent calc(100% / 12 - 1px),
                rgba(255, 255, 255, 0.03) calc(100% / 12 - 1px),
                rgba(255, 255, 255, 0.03) calc(100% / 12)
              )`,
              backgroundSize: '100% 100%',
              maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
            }}
          />
          {/* Animated horizontal grid lines */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(
                0deg,
                transparent 0px,
                transparent 79px,
                rgba(255, 255, 255, 0.02) 79px,
                rgba(255, 255, 255, 0.02) 80px
              )`,
              backgroundSize: '100% 80px',
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)',
            }}
          />

          {/* Moving grid highlight beam */}
          <div
            className="absolute inset-0 animate-grid-beam"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(16, 185, 129, 0.05) 50%, transparent 100%)',
              backgroundSize: '50% 100%',
            }}
          />

          {/* Pulsing emerald accent points */}
          <div
            className="absolute w-2 h-2 bg-emerald-500/30 rounded-full animate-pulse-glow"
            style={{ top: '38.2%', left: '38.2%' }}
          />
          <div
            className="absolute w-2 h-2 bg-emerald-500/30 rounded-full animate-pulse-glow"
            style={{ top: '38.2%', right: '38.2%', animationDelay: '0.5s' }}
          />
          <div
            className="absolute w-2 h-2 bg-emerald-500/30 rounded-full animate-pulse-glow"
            style={{ top: '61.8%', left: '38.2%', animationDelay: '1s' }}
          />
          <div
            className="absolute w-2 h-2 bg-emerald-500/30 rounded-full animate-pulse-glow"
            style={{ top: '61.8%', right: '38.2%', animationDelay: '1.5s' }}
          />

          {/* Corner brackets - design framing (animated) */}
          <div className="corner-bracket absolute top-8 left-8 w-8 h-8 border-l border-t border-white/10 opacity-0" />
          <div className="corner-bracket absolute top-8 right-8 w-8 h-8 border-r border-t border-white/10 opacity-0" />
          <div className="corner-bracket absolute bottom-8 left-8 w-8 h-8 border-l border-b border-white/10 opacity-0" />
          <div className="corner-bracket absolute bottom-8 right-8 w-8 h-8 border-r border-b border-white/10 opacity-0" />
        </div>

        {/* Background Ripple Effect - triggered with shockwave */}
        <BackgroundRipple
          rows={8}
          cols={14}
          onTrigger={triggerRipple}
          rippleColor="rgba(16, 185, 129, 0.08)"
          baseOpacity={0}
          rippleOpacity={0.35}
          animationDuration={500}
          spreadDuration={700}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center px-4">
        {/* Title: NIKO.KNEZ - same style as NK.DESIGN in preloader */}
        <h1
          ref={titleRef}
          className="text-[11vw] md:text-[9vw] lg:text-[7vw] font-bold tracking-tight leading-none flex items-center justify-center mb-8"
          onMouseEnter={() => setCursorState('hovering')}
          onMouseLeave={() => setCursorState('default')}
        >
          {/* NIKO - random placeholder until wave triggers the flip (reverse: O→K→I→N) */}
          <span ref={nikoRef} className="inline-block text-white/90 opacity-0">
            {!triggerScramble ? (
              <span className="text-white/40">{placeholderWords.word1}</span>
            ) : (
              <TextScramble
                text="NIKO"
                duration={800}
                delay={0}
                flipSpeed={60}
                reverseReveal={true}
              />
            )}
          </span>

          {/* Impact Dot Container - with shockwave ring effect */}
          <span className="relative inline-flex items-center justify-center mx-2">
            {/* The actual dot - contains rings as children so they're perfectly centered */}
            <span
              ref={impactDotRef}
              className="relative inline-block text-emerald-500"
              style={{ transform: 'scale(0)', opacity: 0 }}
            >
              .
              {/* Shockwave ring 1 - perfect circle centered on dot */}
              <span
                ref={impactRingRef}
                className="absolute rounded-full border-2 border-emerald-400 pointer-events-none"
                style={{
                  width: '0.5em',
                  height: '0.5em',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) scale(1)',
                  transformOrigin: 'center center',
                  opacity: 0,
                }}
              />
              {/* Shockwave ring 2 - second ripple */}
              <span
                ref={impactRing2Ref}
                className="absolute rounded-full border border-emerald-300/80 pointer-events-none"
                style={{
                  width: '0.5em',
                  height: '0.5em',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) scale(1)',
                  transformOrigin: 'center center',
                  opacity: 0,
                }}
              />
              {/* Glow burst - bright flash on impact */}
              <span
                ref={impactGlowRef}
                className="absolute rounded-full bg-emerald-400/80 blur-lg pointer-events-none"
                style={{
                  width: '0.5em',
                  height: '0.5em',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%) scale(1)',
                  transformOrigin: 'center center',
                  opacity: 0,
                }}
              />
            </span>
          </span>

          {/* KNEZ - random placeholder until wave triggers the flip (normal: K→N→E→Z) */}
          <span ref={knezRef} className="inline-block text-white/90 opacity-0">
            {!triggerScramble ? (
              <span className="text-white/40">{placeholderWords.word2}</span>
            ) : (
              <TextScramble
                text="KNEZ"
                duration={900}
                delay={50}
                flipSpeed={60}
              />
            )}
          </span>
        </h1>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="text-lg md:text-xl text-white/50 tracking-wider max-w-lg mx-auto opacity-0"
        >
          {nikoInfo.title}
        </p>

        {/* Location */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 2, duration: 0.6 }}
          className="flex items-center justify-center gap-2 mt-4"
        >
          <span className="text-sm text-white/30">{nikoInfo.location}</span>
          <span className="text-white/20">•</span>
          <span className="text-sm text-white/30">{nikoInfo.experience} Jahre Erfahrung</span>
        </motion.div>

        {/* Role badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 2.2, duration: 0.6 }}
          className="flex items-center justify-center gap-4 mt-8 flex-wrap"
        >
          {['Fullstack', 'UX/UI', 'React', 'Vue.js', 'TypeScript'].map((tag) => (
            <span
              key={tag}
              className="px-4 py-1.5 text-xs text-white/40 border border-white/10 rounded-full tracking-wider uppercase"
            >
              {tag}
            </span>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div
        ref={scrollIndicatorRef}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-0"
      >
        <span className="text-xs text-white/30 tracking-wider uppercase">
          Scroll
        </span>
        <ArrowDown className="w-5 h-5 text-white/30" strokeWidth={1.5} />
      </div>
    </section>
  );
}
